import { Reminder } from '@griply/types';
import { removeReminder } from '@griply/utils';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BellNotifications16 } from 'shared/assets/icons';
import { RemindersPopupMenu } from 'shared/components/ui/reminders-popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useRemindersTrigger } from 'shared/hooks/use-reminders-trigger';
import { useUser } from 'shared/hooks/use-user';
import { TimeFormatOptions } from 'shared/types/date-format-options';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import { MetaReminderRow } from './meta-reminder-row';

export type MetaReminderProps = {
  timeFormat: TimeFormatOptions;
  hasTime: boolean;
  // Without a date nothing anchors a reminder, so the settings default stays hidden.
  hasDate: boolean;
  reminders?: Reminder[] | null;
  reminderTime?: number | null;
  onChange: (reminders: Reminder[]) => void;
};

export const MetaReminder: React.FC<MetaReminderProps> = ({
  timeFormat,
  hasTime,
  hasDate,
  reminders,
  reminderTime,
  onChange,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const user = useUser();
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();

  const { displayedReminders, openWithMigration, commitReminders } =
    useRemindersTrigger({
      reminders,
      reminderTime,
      timeFormat,
      notifications: user?.settings?.notifications,
      hasDate,
      hasTime,
      onChange,
      openMenu,
    });

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openWithMigration();
      }
    },
    [menuOpen, openWithMigration],
  );

  useKeysDown(['KeyR'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  // Clearing the only row resolves to `[]`, which persists the opt-out.
  const onClearReminder = (reminder: Reminder) =>
    commitReminders(removeReminder(displayedReminders, reminder));

  return (
    <>
      {displayedReminders.map((reminder, index) => (
        <MetaReminderRow
          key={reminder.id ?? `${reminder.basedOn}-${reminder.offset}`}
          reminder={reminder}
          timeFormat={timeFormat}
          hasTime={hasTime}
          showIcon={!index}
          onClear={onClearReminder}
        />
      ))}

      <MetaInteraction
        ref={buttonRef}
        icon={displayedReminders.length ? undefined : BellNotifications16}
        onClick={openWithMigration}
        placeholder={t(
          displayedReminders.length
            ? 'reminders.add'
            : 'dialogs.task-detail.meta.reminder.placeholder',
        )}
        selected={menuOpen}
      />
      {menuOpen && (
        <RemindersPopupMenu
          referenceElement={buttonRef}
          reminders={displayedReminders}
          hasTime={hasTime}
          timeFormat={timeFormat}
          onChange={commitReminders}
          onClose={closeMenu}
        />
      )}
    </>
  );
};
